import { Container } from 'pixi.js';
import type { CardPose } from '../card/pose';
import type { CardView } from '../card/cardView';
import {
  DEFAULT_FEEL,
  approach,
  chase,
  feelTarget,
  pulseDecay,
  type FeelInput,
  type FeelParams,
} from './feel';

/**
 * **会呼吸的牌** —— 一张牌"活着"的那一半，和"摆在哪"完全分开。
 *
 * ## 为什么要单独抽出来
 *
 * 评审：
 *
 * > 我无法 hover 造成濒死效果的、浮在屏幕中央的牌，以查看它的 lore……
 * > 应当使得浮在屏幕中央的牌有正常手牌的一切属性和功能，例如自然摆动、
 * > 随音乐鼓动、hover 有 lore 之类。**不要重复实现功能**，或许你应该将手牌
 * > 的部分实现抽象出来，并令其也适用于屏幕中央的牌。
 *
 * 所以 `HandView` 里那一段"每张牌怎么动"被搬到了这里，两边共用：
 *
 * ```
 *   owner 每帧说：这一张**该摆成什么样**（槽位姿态 / 正中那一格）
 *                 这一张**被指着没有 / 被按着没有 / 拖到哪去了**
 *        │
 *        ▼
 *   CardRig 负责：hover / 按下包络 → feelTarget → chase / approach
 *                 → 空闲摇摆 + 转向鼠标 + 邻居避让 + 音乐脉动 → card.setPose()
 * ```
 *
 * ## 它**不**管什么
 *
 * | 不管 | 归谁 |
 * | --- | --- |
 * | 扇形摆法（间距 / 垂度 / 锚点） | `hand/layout.ts` + 调用方 |
 * | 扇形整体上移、邻居避让**要不要**发生 | 调用方（算好两个 0..1 包络传进来） |
 * | 命中测试、点选、拖动换位、出牌 | 调用方（手牌那套的规则见 `handView.ts`） |
 * | 牌面纹理、大小、`CardView` 的创建 | 调用方 |
 *
 * 于是"浮在正中的那张异常牌"只要给它一格槽位、一个命中测试，就自动有了
 * 自然摆动、跟着音乐鼓动、hover 抬起这一整套 —— 一行手感代码都不用重写。
 */

/**
 * **一帧最多推进多少秒。**
 *
 * ⚠️ `deltaMS` 必须**夹住**：切走标签页再回来，它可能是好几秒。不夹的话牌会
 *    瞬移、"最大速度"那一整套限制会被绕过。手牌和正中那张牌用的是同一个值。
 */
export const MAX_STEP_SECONDS = 0.1;

/** 每张牌的**状态**（姿态是每帧从它算出来的，不存） */
export interface CardRigState {
  pos: [number, number];
  vel: [number, number];
  /** 当前大小 */
  scale: number;
  /**
   * 平滑过的三个角度（弧度）。
   *
   * ⚠️ 它们**必须**和位置一样平滑：换位时那张牌的槽位换了一位，
   * 扇形的倾角跟着变一格 —— 位置是滑过去的、倾角却是瞬间跳，一眼假。
   */
  yaw: number;
  pitch: number;
  roll: number;
  /** hover 包络 0..1 */
  hover: number;
  /** 按下包络 0..1 */
  press: number;
  /**
   * **静止时该在哪**（这一帧 `feelTarget` 算出来的那个点，拖动时不算）。
   *
   * 拖动时实际位置在 `pos`、它留在这儿不动 —— 两者的差就是"被拎起来多远"。
   * 联机时那个差要发给对面，让对方屏幕上那一手牌背**逐帧重现同一个拖拽动作**。
   */
  rest: [number, number];
}

/**
 * 这一帧给某一张牌的交代。
 *
 * `input` 里**不含** `t` / `own` / `press` / `group` / `avoid` ——
 * 那五个由 rig 自己填（时间它在推、包络它在算、整扇上移是调用方每帧给的一个数）。
 */
export interface CardStep {
  /** 这一张"该摆成什么样"（扇形槽位 / 正中那一格） */
  readonly posture: CardPose;
  /** `feelTarget` 的其余输入（位次、pointer、unit/wide……） */
  readonly input: Omit<FeelInput, 't' | 'own' | 'press' | 'group' | 'avoid'>;
  /** hover 的目标 0 或 1（进是瞬时的、出按 `fallRate`，见 `step`） */
  readonly wantHover: number;
  /** 按下的目标 0 或 1 */
  readonly wantPress: number;
  /**
   * 位置覆盖。拖着的那张牌走指针，不走槽位（"手指底下的牌就该在手指底下"）。
   * 不给就用 `feelTarget` 算出来的槽位位置。
   */
  readonly override?: readonly [number, number];
  /** 叠放次序。不给就按牌序 */
  readonly z?: number;
  /**
   * **这一帧位置直接到位**（不补间）。
   *
   * 评审要的两件事都靠它：「hover 时移动和尺寸缩放立即到位」和「手牌扇上抬
   * 无动画，归位才有动画」—— 后者是因为整扇上移会把每张牌的目标位置整体抬高，
   * 光让包络跳上去没用，位置还在 `chase` 里慢慢追。
   *
   * ⚠️ 只管**位置**：角度和大小照旧补间（`hoverEntering` 那条另算）。
   */
  readonly snapPos?: boolean;
}

export interface CardRigOptions {
  /**
   * **牌挂在哪个容器里。**
   *
   * ⚠️ 容器是**调用方给的**（不是 rig 自己造的）：手牌那一层要它的牌仍然直接
   *    挂在 `label = 'hand'` 的节点下面（场景读数、调试页、冒烟都按那个节点找牌），
   *    而浮在正中那张牌有它自己的容器。rig 只管"把牌放进去、让它动"。
   */
  readonly view: Container;
  /** 手感参数。不给用默认（`DEFAULT_FEEL`） */
  readonly feel?: Partial<FeelParams>;
}

export class CardRig {
  /** 挂牌的容器（调用方给的）。⚠️ 排序要开着：被 hover / 被抓住的牌要整体到最上面 */
  readonly view: Container;

  readonly #cards: CardView[] = [];
  readonly #state: CardRigState[] = [];
  #feel: FeelParams;
  /** 空闲摇摆的时间轴（秒） */
  #t = 0;
  /**
   * 音乐脉动（Bar 事件）。1 = 刚敲下去，往后指数衰减到 0。
   *
   * ⚠️ 它是**一整组牌共用一个**的：鼓点是全局的，不是每张牌各敲各的。
   */
  #pulse = 0;

  constructor(options: CardRigOptions) {
    this.#feel = { ...DEFAULT_FEEL, ...options.feel };
    this.view = options.view;
  }

  // ---- 牌 ----

  /** 这一组里现在有哪些牌（索引 = 加进来的顺序） */
  get cards(): readonly CardView[] {
    return this.#cards;
  }

  get count(): number {
    return this.#cards.length;
  }

  /** 追加一张牌，并给它一份初始状态。返回它的索引 */
  add(card: CardView): number {
    this.#cards.push(card);
    this.#state.push({
      pos: [0, 0],
      vel: [0, 0],
      scale: 1,
      yaw: 0,
      pitch: 0,
      roll: 0,
      hover: 0,
      press: 0,
      rest: [0, 0],
    });
    this.view.addChild(card);
    return this.#cards.length - 1;
  }

  /**
   * **把某张牌瞬移到一个姿态**（重建手牌、重置手感时用：不留补间、包络清零）。
   *
   * ⚠️ 它和"每帧追目标"是两回事：这里直接写死，所以下一帧之前牌就在那儿。
   */
  settle(index: number, pose: CardPose): void {
    const st = this.#state[index];
    const card = this.#cards[index];
    if (st === undefined) return;
    st.pos = [pose.x, pose.y];
    st.vel = [0, 0];
    st.scale = pose.scale;
    st.yaw = pose.yaw;
    st.pitch = pose.pitch;
    st.roll = pose.roll;
    st.hover = 0;
    st.press = 0;
    st.rest = [pose.x, pose.y];
    card?.setPose(pose);
  }

  /**
   * **这一帧每个位次的状态快照**（重建那一路要用，见 `settleInherit`）。
   *
   * ⚠️ 是**拷贝**：调用方拿它去喂给重建之后的新实例，不能让两边共享同一个
   *    数组（不然老状态被新对象写坏了）。
   */
  snapshot(): CardRigState[] {
    return this.#state.map((st) => ({
      pos: [st.pos[0], st.pos[1]],
      vel: [st.vel[0], st.vel[1]],
      scale: st.scale,
      yaw: st.yaw,
      pitch: st.pitch,
      roll: st.roll,
      hover: st.hover,
      press: st.press,
      rest: [st.rest[0], st.rest[1]],
    }));
  }

  /**
   * **像 `settle` 一样落到槽位，但把上一帧的"在途状态"接过来。**
   *
   * 评审报的那个 bug：手牌一刷新（`HandView.setCards` / 改 UI 倍率走的重建），
   * 牌扇**先无动画地掉回槽位**、再**重放一次上抬**——因为重建把每张牌的
   * 位置 / 大小 / hover 包络全清零了。
   *
   * 所以重建时先把旧状态 `snapshot()` 出来，这里按位次接上：位置、大小、
   * 角度、hover / press 包络全部沿用（于是下一帧的 `chase` / `approach`
   * 从**当前所在的地方**继续，而不是从槽位重新出发）。
   *
   * ⚠️ 位次对不上时（张数变了、顺序换了）调用方**别传** `prev` —— 那种情况
   *    本来就该落到槽位（新牌从它那一格出现）。
   */
  settleInherit(index: number, pose: CardPose, prev: CardRigState | undefined): void {
    const st = this.#state[index];
    if (st === undefined) return;
    if (prev === undefined) {
      this.settle(index, pose);
      return;
    }
    st.pos = [prev.pos[0], prev.pos[1]];
    st.vel = [prev.vel[0], prev.vel[1]];
    st.scale = prev.scale;
    st.yaw = prev.yaw;
    st.pitch = prev.pitch;
    st.roll = prev.roll;
    st.hover = prev.hover;
    st.press = prev.press;
    st.rest = [pose.x, pose.y];
  }

  /** 某张牌的现场状态（读数用：`dragInfo` 要读它和 `rest` 的差） */
  state(index: number): Readonly<CardRigState> | undefined {
    return this.#state[index];
  }

  /** 把牌全部摘下来（**不销毁** —— 牌面归调用方所有）。状态一并清空 */
  clear(): void {
    for (const card of this.#cards) card.removeFromParent();
    this.#cards.length = 0;
    this.#state.length = 0;
  }

  // ---- 参数与读数 ----

  get feel(): Readonly<FeelParams> {
    return this.#feel;
  }

  applyFeel(next: Partial<FeelParams>): void {
    this.#feel = { ...this.#feel, ...next };
  }

  /** 空闲摇摆走到哪儿了（秒）。调试与测试用 */
  get time(): number {
    return this.#t;
  }

  /**
   * **音乐脉动（Bar 事件）**：这一组牌**立即**放大一下，然后 easeout 缩回去。
   *
   * ⚠️ 放大是硬赋的、不走平滑 —— 评审要的是"无动画地立即稍微放大"。
   * 只有缩回去有动画（指数衰减，而且是在**这一帧画完之后**才衰减，
   * 见 `step` 的结尾）。
   *
   * ⚠️ 这一层**不许 import 音频层**：牌不知道音乐存在，是音乐来敲牌。
   */
  pulse(): void {
    this.#pulse = 1;
  }

  get pulseValue(): number {
    return this.#pulse;
  }

  // ---- 每帧 ----

  /**
   * 推进一帧。
   *
   * @param dt   秒。调用方**必须夹住**它（切走标签页再回来可能是好几秒，
   *             不夹的话牌会瞬移、"最大速度"那一整套限制会被绕过）
   * @param group  整组牌的上移包络 0..1（手牌那条只看 y 的横带；正中那张是 0）
   * @param avoid  邻居避让的包络 0..1（正中那张是 0）
   * @param specs  一张牌一份，见 `CardStep`
   */
  step(dt: number, group: number, avoid: number, specs: readonly CardStep[]): void {
    const n = this.#cards.length;
    if (n === 0) return;
    this.#t += dt;
    const feel = this.#feel;

    for (let i = 0; i < n; i++) {
      const card = this.#cards[i];
      const st = this.#state[i];
      const spec = specs[i];
      if (card === undefined || st === undefined || spec === undefined) continue;

      /**
       * ★ **hover 是瞬时的，解除 hover 才补间**（评审：「hover 手牌时的动画
       * 时间改为 0」）。所以"上"直接赋值、"下"照旧按 `fallRate` 落回去。
       */
      const hoverEntering = spec.wantHover > st.hover;
      st.hover = hoverEntering ? spec.wantHover : approach(st.hover, spec.wantHover, dt, feel.fallRate);
      st.press = approach(
        st.press,
        spec.wantPress,
        dt,
        spec.wantPress > st.press ? feel.riseRate : feel.fallRate,
      );

      const target = feelTarget(
        spec.posture,
        { ...spec.input, t: this.#t, own: st.hover, press: st.press, group, avoid },
        feel,
      );

      // 拖动中：目标是指针 + 抓住时的偏移。
      // ⚠️ 这里**故意抛开**槽位里那些抬升 —— 手指底下的牌就该在手指底下
      const to = spec.override ?? [target.x, target.y];

      st.rest = [target.x, target.y];

      if (hoverEntering || spec.snapPos === true) {
        /**
         * ★ **hover 直接产生的那两样——位移与缩放——立即到位，不做动画。**
         *
         * 评审第四轮：「删去卡牌被 hover 时移动和尺寸缩放的动画，立即到位
         * （仅影响 hover 直接产生的动画，取消 hover 时的动画，以及其他的移动
         * 和缩放动画不应受影响）」。
         *
         * 所以只在这一帧（`hover 0 → 1` 的那一帧）把位置与大小**硬赋**成目标值；
         * `hover` 落下去那一路仍然走 `chase` / `approach`（"取消 hover 时的
         * 动画"不受影响），抽牌、换位、让位那些位移也照旧走平滑。
         *
         * ⚠️ 角度（yaw/pitch/roll）**不在这里**：评审说的是"移动和尺寸缩放"，
         *    而"牌转向鼠标"那一点倾斜正是 hover 的观感来源，跳变反而像卡了一下。
         */
        st.pos = [to[0], to[1]];
        st.vel = [0, 0];
        // 大小只在 hover 上来那一帧硬赋；整扇上移不改大小
        if (hoverEntering) st.scale = target.scale;
      } else {
        const step = chase(st.pos, to, dt, feel.followRate, feel.maxSpeed * spec.input.unit);
        st.pos = [step.pos[0], step.pos[1]];
        st.vel = [step.vel[0], step.vel[1]];

        // ⚠️ 大小和位置一样只做**单调**趋近 —— 不过冲、不抖。
        //    试过欠阻尼弹簧（"抖一两下"），评审看了说不好看，删了
        st.scale = approach(st.scale, target.scale, dt, feel.scaleRate);
      }
      // 角度也平滑 —— 不平滑的话换位时倾角会瞬间跳一格（位置在滑、角度在跳）
      st.yaw = approach(st.yaw, target.yaw, dt, feel.angleRate);
      st.pitch = approach(st.pitch, target.pitch, dt, feel.angleRate);
      st.roll = approach(st.roll, target.roll, dt, feel.angleRate);

      card.setPose({
        ...target,
        x: st.pos[0],
        y: st.pos[1],
        // ⚠️ 脉动是**硬起**的（不在 `st.scale` 里，所以不过平滑），
        //    乘在最外面 —— 它和"追目标大小"是两件互不干涉的事
        scale: st.scale * (1 + feel.barPulseScale * this.#pulse),
        yaw: st.yaw,
        pitch: st.pitch,
        roll: st.roll,
      });
      card.zIndex = spec.z ?? i;
    }

    // ⚠️ 脉动在**这一帧画完之后**才衰减。
    //    放在前面的话，`pulse()` 之后的第一帧就已经掉了一格 ——
    //    "立即放大 5%"实际只放大 4.5%，参数就不再是它字面上的意思了
    this.#pulse = pulseDecay(this.#pulse, dt, feel.barPulseRate);
  }

  /** 摘掉引用。**不销毁容器**（那是调用方的） */
  destroy(): void {
    this.#cards.length = 0;
    this.#state.length = 0;
  }
}
